import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import * as localService from "../../services/localService";
import { logoutUser, resetTotalAmount, saveCartDetails, setCartEntries } from "../../redux/actions/dashboard";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "../../shadcn/ui/card";
import { Button } from "../../shadcn/ui/button";
import { Input } from "../../shadcn/ui/input";
import { Label } from "../../shadcn/ui/label";
import { Badge } from "../../shadcn/ui/badge";
import {
  Check,
  ArrowRight,
  Users,
  Building2,
  Shield,
  Zap,
  Headphones,
  CreditCard,
  Sparkles,
  Gift,
  Plus,
  Minus,
  Mail,
  Share2,
} from "lucide-react";
import billingService from "../../services/billingService";
import { showApiMessage } from "../../utils/messageHandler";
import { Container } from "../../components/ui/container";
import logger from "../../utils/logger";
import { getSubscriptionQuantity, isQuantityEditable } from "../../utils/subscriptionUtils";
import { isOrgAdmin } from "../../utils/orgAdminAccess";
import ContactAdminBanner from "../../components/ContactAdminBanner";
import ContactAdminModal from "../../components/ContactAdminModal";
import toastUtils from "../../utils/toast";

const SubscriberPricingPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const user = localService.getUser();
  const userIsOrgAdmin = isOrgAdmin(user);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [selectedPkgForModal, setSelectedPkgForModal] = useState(null);
  const [packages, setPackages] = useState([]);
  const [packageQuantities, setPackageQuantities] = useState({}); // Track quantity for each standard package
  const [addOns, setAddOns] = useState([]);
  const [addOnQuantities, setAddOnQuantities] = useState({}); // Track quantity for each add-on
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchApplicablePackages = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch recurring packages (subscriptions)
        const recurringResponse = await billingService.getApplicablePackages({
          recurring: true,
          limit: 10,
        });

        // Fetch non-recurring packages (add-ons) using the filter Padmore added
        const addOnsResponse = await billingService.getApplicablePackages({
          recurring: false,
          limit: 10,
        });

        if (recurringResponse.data?.data) {
          const packagesData = recurringResponse.data.data;
          setPackages(packagesData);

          // Initialize quantities for standard packages
          const user = localService.getUser();
          const initialPkgQuantities = {};
          packagesData.forEach(pkg => {
            initialPkgQuantities[pkg.id] = getSubscriptionQuantity(user, pkg);
          });
          setPackageQuantities(initialPkgQuantities);
        }

        if (addOnsResponse.data?.data) {
          const addOnsData = addOnsResponse.data.data;
          setAddOns(addOnsData);
          // Initialize quantities to 1 for all add-ons
          const initialQuantities = {};
          addOnsData.forEach(addOn => {
            initialQuantities[addOn.id] = 1;
          });
          setAddOnQuantities(initialQuantities);
        }
      } catch (err) {
        setError("Failed to load packages. Please try again.");
        showApiMessage(err, "load packages");
      } finally {
        setLoading(false);
      }
    };
    fetchApplicablePackages();
  }, []);

  const handleGetStarted = async (packageId, pkg) => {
    // Restrict subscription purchase to Organization Admins
    if (!userIsOrgAdmin) {
      setSelectedPkgForModal(pkg);
      setContactModalOpen(true);
      return;
    }

    try {
      setLoading(true);
      const user = localService.getUser();
      if (!user?.organizationId) {
        showApiMessage({ response: { status: 400, data: { message: "Organization ID not found" } } }, "subscribe");
        return;
      }

      // For add-ons (recurring = false), use quantity from state. For recurring packages, use tracked quantity if editable, else calculated
      const isAddOn = pkg?.recurring === false;
      const subscriptionQuantity = getSubscriptionQuantity(user, pkg);

      let quantity = 1;
      if (isAddOn) {
        quantity = addOnQuantities[pkg.id] || 1;
      } else {
        // For standard packages
        if (isQuantityEditable(user, pkg)) {
          quantity = packageQuantities[pkg.id] || 1;
        } else {
          quantity = subscriptionQuantity;
        }
      }
      const userRole = user?.organizationRole?.code || user?.role || "";

      logger.log("Creating subscription with:", {
        organizationId: user.organizationId,
        packageId,
        quantity: quantity,
        userRole: userRole,
      });

      const response = await billingService.createOrganizationSubscription({
        organizationId: user.organizationId,
        packageId,
        quantity: quantity,
      });

      showApiMessage(response, "create subscription");

      if (response.data?.data?.checkoutUrl) {
        const checkoutUrl = response.data.data.checkoutUrl;
        // Open Paystack checkout in a new tab
        const newWindow = window.open("", "_blank");
        if (newWindow) {
          newWindow.location.href = checkoutUrl;
        } else {
          // Fallback if popup blocker prevents new window
          window.location.href = checkoutUrl;
        }
      } else {
        // If no checkout URL, redirect to dashboard
        history.push("/dashboard");
      }
    } catch (error) {
      showApiMessage(error, "create subscription");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Clear all user data and reset redux, same as AuthenticatedLayout
      localService.clearAllUserData();
      dispatch(logoutUser());
      dispatch(saveCartDetails([]));
      dispatch(setCartEntries());
      dispatch(resetTotalAmount());
      // Small delay to ensure state flush
      await new Promise(resolve => setTimeout(resolve, 100));
      history.push("/signin");
    } catch (err) {
      showApiMessage(err, "logout");
    }
  };

  const formatCurrency = (amount, currencyCode = "KES") => {
    if (!amount) return "KES 0";
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;

    // Handle currency code - it might be an object with a 'code' property
    let currency = "KES";
    if (typeof currencyCode === "string") {
      currency = currencyCode;
    } else if (currencyCode && typeof currencyCode === "object" && currencyCode.code) {
      currency = currencyCode.code;
    }

    // Show the amount as-is without any calculations
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(numAmount);
  };

  const getPackageFeatures = pkg => {
    const features = [];

    if (pkg.maximumTeamSize) {
      features.push({
        text: `${pkg.maximumTeamSize} team members`,
        icon: Users,
        description: "Maximum number of team members allowed",
      });
    }

    if (pkg.maximumServiceProvidersAllowed) {
      features.push({
        text: `${pkg.maximumServiceProvidersAllowed} service providers`,
        icon: Building2,
        description: "Maximum number of service providers",
      });
    }

    if (pkg.offers && pkg.offers.length > 0) {
      pkg.offers.forEach(offer => {
        if (offer.name) {
          features.push({
            text: offer.name,
            icon: Zap,
            description: offer.description || "Included service offer",
          });
        }
      });
    }

    // Add default features if none exist
    if (features.length === 0) {
      features.push(
        {
          text: "Core features included",
          icon: Check,
          description: "All essential platform features",
        },
        {
          text: "Basic support",
          icon: Headphones,
          description: "Email and chat support",
        },
        {
          text: "Standard templates",
          icon: Building2,
          description: "Access to standard templates",
        },
        {
          text: "Email support",
          icon: Check,
          description: "24/7 email support",
        },
        {
          text: "Cloud storage",
          icon: Shield,
          description: "Secure cloud storage included",
        }
      );
    }

    return features;
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="px-4 py-16 mx-auto max-w-7xl">
          <div className="animate-pulse">
            <div className="mb-4 w-1/3 h-8 bg-gray-200 rounded mx-auto"></div>
            <div className="mb-16 w-1/2 h-6 bg-gray-200 rounded mx-auto"></div>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {[1, 2, 3].map(index => (
                <div key={index} className="space-y-6">
                  <div className="rounded-2xl p-5 shadow-sm border h-[280px] bg-white border-gray-100">
                    <div className="space-y-4">
                      <div className="w-32 h-6 bg-gray-200 rounded animate-pulse"></div>
                      <div className="w-24 h-9 bg-gray-200 rounded animate-pulse"></div>
                      <div className="space-y-2">
                        <div className="w-full h-4 bg-gray-200 rounded animate-pulse"></div>
                        <div className="w-3/4 h-4 bg-gray-200 rounded animate-pulse"></div>
                      </div>
                      <div className="mt-auto w-full h-12 bg-gray-200 rounded-xl animate-pulse"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen">
        <div className="px-4 py-16 mx-auto max-w-7xl">
          <div className="text-center">
            <h2 className="mb-4 text-2xl font-bold text-gray-900">Unable to load packages</h2>
            <p className="mb-4 text-gray-600">{error}</p>
            <div className="flex items-center justify-center gap-3">
              <Button onClick={() => window.location.reload()}>Try Again</Button>
              <Button variant="default" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleLogout}>
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Container className="px-4 py-16 mx-auto max-w-7xl">
        {/* Pricing Header */}
        <div className="mb-12 text-center relative">
          <div className="absolute top-0 right-0 flex items-center gap-2">
            {window.location.hostname === "localhost" && (
              <Button
                variant="outline"
                onClick={() => {
                  localStorage.setItem("debug_skip_subscription", "true");
                  history.push("/startbooking");
                }}
                className="bg-orange-50 border-orange-300 text-orange-600 hover:bg-orange-100"
              >
                Skip Subscription (Debug)
              </Button>
            )}
            <Button variant="outline" onClick={handleLogout} className="bg-white hover:bg-gray-50 border-gray-300">
              Logout
            </Button>
          </div>
          <p className="mb-2 text-sm text-gray-700">Pricing</p>
          <h1 className="mb-4 text-4xl font-bold text-gray-900">Choose your plan</h1>
          <p className="text-base text-gray-700">Select a standard package to get started with Spacia</p>
        </div>

        {/* Contact Admin Banner for non-admin sub-accounts */}
        {!userIsOrgAdmin && (
          <div className="mb-8">
            <ContactAdminBanner user={user} />
          </div>
        )}

        {/* Dynamic Pricing Cards */}
        {packages.length > 0 ? (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {packages.map(pkg => {
              const isEnterprise = pkg.name?.toLowerCase().includes("enterprise");
              const features = getPackageFeatures(pkg);

              return (
                <Card
                  key={pkg.id}
                  className={`relative transition-all ${
                    isEnterprise ? "border-blue-500 shadow-lg" : "border-gray-200 shadow-sm"
                  } ${!userIsOrgAdmin ? "opacity-80 bg-gray-50/50 hover:opacity-95" : ""}`}
                >
                  {isEnterprise && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-blue-500 text-white">Most Popular</Badge>
                    </div>
                  )}

                  <CardHeader className="text-center pb-4">
                    <CardTitle className="text-2xl font-bold text-gray-900">{pkg.name}</CardTitle>
                    <CardDescription className="text-gray-600 mt-2">
                      {pkg.description || "Comprehensive package with all the features you need"}
                    </CardDescription>
                    <div className="mt-4">
                      <div className="flex items-baseline justify-center gap-2">
                        <span className="text-5xl font-bold text-gray-900">
                          {formatCurrency(pkg.amount, pkg.currency)}
                        </span>
                        <span className="text-xl text-gray-600">{pkg.recurring ? "/month" : "one-time"}</span>
                      </div>
                      {pkg.recurring && <p className="text-sm text-gray-500 mt-2">Billed monthly • Cancel anytime</p>}
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0">
                    {/* Quantity Selector for Standard Packages */}
                    {(() => {
                      const user = localService.getUser();
                      const canEditQuantity = user && isQuantityEditable(user, pkg);
                      const quantity = packageQuantities[pkg.id] || 1;

                      if (canEditQuantity) {
                        return (
                          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <Label
                              htmlFor={`pkg-quantity-${pkg.id}`}
                              className="text-sm font-medium text-gray-700 mb-2 block"
                            >
                              Number of Users
                            </Label>
                            <div className="flex items-center gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-9 w-9 flex-shrink-0"
                                onClick={() => {
                                  const newQuantity = Math.max(1, quantity - 1);
                                  setPackageQuantities(prev => ({ ...prev, [pkg.id]: newQuantity }));
                                }}
                                disabled={quantity <= 1}
                              >
                                <Minus className="w-4 h-4" />
                              </Button>
                              <Input
                                id={`pkg-quantity-${pkg.id}`}
                                type="number"
                                min="1"
                                value={quantity}
                                onChange={e => {
                                  const newQuantity = parseInt(e.target.value, 10) || 1;
                                  setPackageQuantities(prev => ({ ...prev, [pkg.id]: Math.max(1, newQuantity) }));
                                }}
                                className="w-full text-center"
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-9 w-9 flex-shrink-0"
                                onClick={() => {
                                  setPackageQuantities(prev => ({ ...prev, [pkg.id]: quantity + 1 }));
                                }}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        );
                      } else if (pkg.recurring) {
                        return (
                          <div className="mb-4 flex items-center gap-2 py-2 px-3 bg-gray-50 rounded-md border border-gray-100">
                            <Users className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-500">
                              {pkg.maximumTeamSize > 1 ? "Fixed team size" : `Team size: ${quantity}`}
                            </span>
                          </div>
                        );
                      }
                      return null;
                    })()}

                    <div className="space-y-4">
                      {features.map((feature, featureIndex) => {
                        const IconComponent = feature.icon;
                        return (
                          <div key={featureIndex} className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-5 h-5 mt-0.5">
                              <IconComponent className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{feature.text}</p>
                              <p className="text-xs text-gray-500">{feature.description}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>

                  <CardFooter className="pt-6">
                    <Button
                      onClick={() => handleGetStarted(pkg.id, pkg)}
                      className="w-full"
                      variant={!userIsOrgAdmin ? "outline" : isEnterprise ? "default" : "outline"}
                      disabled={loading}
                    >
                      {!userIsOrgAdmin ? (
                        <Mail className="w-4 h-4 mr-2 text-blue-600" />
                      ) : (
                        <CreditCard className="w-4 h-4 mr-2" />
                      )}
                      {loading
                        ? "Processing..."
                        : userIsOrgAdmin
                          ? "Get Started"
                          : "Contact Admin to Upgrade"}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <h3 className="mb-2 text-xl font-semibold text-gray-900">No packages available</h3>
            <p className="text-gray-600">No standard packages are currently available.</p>
            <div className="mt-6">
              <p className="mb-4 text-sm text-gray-500">
                If you believe this is an error, please contact support or try logging out and back in.
              </p>
              <Button variant="default" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleLogout}>
                Logout
              </Button>
            </div>
          </div>
        )}

        {/* Add-ons Section */}
        <div className="mt-20">
          {/* Section Header */}
          <div className="mb-8 text-center">
            <div className="inline-flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-purple-600" />
              <h2 className="text-3xl font-bold text-gray-900">Add-ons</h2>
              <Sparkles className="w-5 h-5 text-purple-600" />
            </div>
            <p className="text-sm text-gray-600">Enhance your subscription with additional features and services</p>
          </div>

          {/* Add-ons Grid or Empty State */}
          {addOns.length > 0 ? (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {addOns.map(pkg => {
                const features = getPackageFeatures(pkg);

                return (
                  <Card
                    key={pkg.id}
                    className="relative border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-white shadow-md hover:shadow-lg transition-shadow"
                  >
                    {/* Badge */}
                    <div className="absolute -top-3 right-4">
                      <Badge className="bg-purple-600 text-white flex items-center gap-1">
                        <Gift className="w-3 h-3" />
                        One-time
                      </Badge>
                    </div>

                    <CardHeader className="text-center pb-4 pt-6">
                      <CardTitle className="text-2xl font-bold text-gray-900">{pkg.name}</CardTitle>
                      <CardDescription className="text-gray-600 mt-2">
                        {pkg.description || "Enhance your subscription with additional features"}
                      </CardDescription>
                      <div className="mt-4">
                        <div className="flex items-baseline justify-center gap-2">
                          <span className="text-5xl font-bold text-purple-700">
                            {formatCurrency(pkg.amount, pkg.currency)}
                          </span>
                        </div>
                        <p className="text-sm text-purple-600 mt-2 font-medium">Single payment • No renewal</p>
                      </div>
                    </CardHeader>

                    <CardContent className="pt-0">
                      {/* Quantity Input for Add-ons */}
                      <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <Label htmlFor={`quantity-${pkg.id}`} className="text-sm font-medium text-gray-700 mb-2 block">
                          Quantity
                        </Label>
                        <div className="flex items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 flex-shrink-0"
                            onClick={() => {
                              const currentQty = addOnQuantities[pkg.id] || 1;
                              const newQuantity = Math.max(1, currentQty - 1);
                              setAddOnQuantities(prev => ({ ...prev, [pkg.id]: newQuantity }));
                            }}
                            disabled={(addOnQuantities[pkg.id] || 1) <= 1}
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <Input
                            id={`quantity-${pkg.id}`}
                            type="number"
                            min="1"
                            value={addOnQuantities[pkg.id] || 1}
                            onChange={e => {
                              const newQuantity = parseInt(e.target.value) || 1;
                              setAddOnQuantities(prev => ({ ...prev, [pkg.id]: Math.max(1, newQuantity) }));
                            }}
                            className="w-full text-center"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-9 w-9 flex-shrink-0"
                            onClick={() => {
                              const currentQty = addOnQuantities[pkg.id] || 1;
                              setAddOnQuantities(prev => ({ ...prev, [pkg.id]: currentQty + 1 }));
                            }}
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        {pkg.maximumTeamSize && (
                          <p className="text-xs text-gray-500 mt-1">Maximum: {pkg.maximumTeamSize} items</p>
                        )}
                      </div>

                      <div className="space-y-4">
                        {features.slice(0, 4).map((feature, featureIndex) => {
                          const IconComponent = feature.icon;
                          return (
                            <div key={featureIndex} className="flex items-start gap-3">
                              <div className="flex-shrink-0 w-5 h-5 mt-0.5">
                                <IconComponent className="w-5 h-5 text-purple-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{feature.text}</p>
                                <p className="text-xs text-gray-500">{feature.description}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>

                    <CardFooter className="pt-6">
                      <Button
                        onClick={() => handleGetStarted(pkg.id, pkg)}
                        className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                        disabled={loading}
                      >
                        <Gift className="w-4 h-4 mr-2" />
                        {loading
                          ? "Processing..."
                          : userIsOrgAdmin
                            ? "Purchase"
                            : "Contact Admin to Purchase"}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-2xl border border-gray-200">
              <Sparkles className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <h3 className="mb-2 text-xl font-semibold text-gray-900">No add-ons available</h3>
              <p className="text-gray-600">There are currently no add-ons available for purchase.</p>
            </div>
          )}
        </div>

        {/* Additional Info */}
        <div className="mt-16 text-center">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Secure & Reliable</h3>
              <p className="text-sm text-gray-600">Enterprise-grade security and 99.9% uptime guarantee</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Headphones className="w-6 h-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">24/7 Support</h3>
              <p className="text-sm text-gray-600">Round-the-clock customer support when you need it</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Easy Setup</h3>
              <p className="text-sm text-gray-600">Get started in minutes with our intuitive platform</p>
            </div>
          </div>
        </div>

        <ContactAdminModal
          isOpen={contactModalOpen}
          onClose={() => setContactModalOpen(false)}
          user={user}
          packageName={selectedPkgForModal?.name}
        />
      </Container>
    </div>
  );
};

export default SubscriberPricingPage;
